// Field types and a lookup utility function.
const fieldTypes = [
	{ id: 'id', text: 'ID', icon: 'variable', hint: 'Internally used identifier for this object that is unique.', internal: true },
	{ id: 'reference', text: 'Reference', icon: 'link', hint: "Link to another object.", internal: true },
	{ id: 'text', text: 'Text', icon: 'description', hint: 'Stores a single line of text.' },	
	{ id: 'textarea', text: 'Text Area', icon: 'description', hint: 'Stores large amounts of text, multiple lines and paragraphs.' },
	{ id: 'email', text: 'Email', icon: 'email', hint: 'Stores email addresses.' },
	{ id: 'phone', text: 'Phone', icon: 'tel', hint: 'Stores phone numbers with or without country codes.' },
	{ id: 'number', text: 'Number', icon: 'variable', hint: 'Stores numerical values with or without decimal points.' },
	{ id: 'currency', text: 'Currency', icon: 'money', hint: 'Stores currency values with optional currency code.' },
	{ id: 'datetime', text: 'Date & Time', icon: 'event', hint: 'Stores date and/or time values, with optional calendar appointment integration.' },
	{ id: 'attachment', text: 'Attachment', icon: 'attach', hint: 'Stores files as attachments.' },
    { id: 'relationship', text: 'Relationship', icon: 'link', hint: 'Relationship to one or more other blueprints that can itself contain data.' },
];
const fieldTypeMeta = (requestedType) => {
	requestedType = String(requestedType).toLowerCase().trim();
	requestedType = fieldTypes.find(t => t.id == requestedType);
	return requestedType || {};
}

// Field extra types that can be added to a field's spec.
const fieldExtras = [
	{ text: 'Default Values', icon: 'isblank', id: 'default', properties: { value: '' }, restrict: [] },
    { text: 'Restricted Values', icon: 'orderedlist', id: 'restricted', properties: { 'values': [] }, restrict: ['text'] },
	{ text: 'Show & Hide', icon: 'eyeclose', id: 'showhide', properties: { create: false, edit: false, view: false, grid: false }, restrict: [] },
	{ text: 'Uniqueness', icon: 'doesnotcontain', id: 'unique', properties: {}, restrict: [] },
	{ text: 'Auto-increment', icon: 'chevrondoubleright', id: 'autoinc', properties: { code: '' }, restrict: ['text', 'textarea', 'number'] },
	{ text: 'Changes Lock', icon: 'lock', id: 'changes', properties: { allowGroupAdmin: false }, restrict: [] },
];

// These constants are the field names that are reserved for internal use. They should match the backends list in App\Model\Field.
const fieldNamesReserved = [
	'id', 'created_at', 'updated_at', 'user_id', 'type_id', 'sequence', 'group_id',							// These are internal fields that are merged in and shouldn't be present.
	'badge', 'html', 'text', 'disabled', 'key', 'showchevron', 'show_chevron', 'template', 'visible',		// These are common display options used in DevExpress and can cause conflicts.
];

// TODO: Refactor this?
// The attachment data contains a lot of metadata that we don't want to bother to serialize in our field data for each mote.
function stripFileFieldData(attachmentData) {
	return {
		id: attachmentData.id,
		filename: attachmentData.filename,
		filesize: attachmentData.filesize,
		store: attachmentData.store,
	};
}

// Field handling class for organization and interpreting field data. These fields are stored in the schema cache.
// In general, these are read only - we don't use caches to store or read schema/fields in the administrative interfaces, opting to get fresh copies directly always.
import { SchemaCache } from '@/schemas';

class Field {
    id = 0;
	field = '';
	fieldtype = '';
	label = '';
	spec = [];
	schema_id = 0;
	keyspec = '';			// We automatically derive the keyspec from the schema and field name on creation.
	schema_type = '';		// We automatically derive the schema type (first part of the keyspec) from the schema ID on creation.
	
    // Constructor for loading field object from server data. You can't create a new empty field this way; see field-creator.vue for that.
    constructor(record) {
        for (let key in record) {
            if (key in this) this[key] = record[key];
        }
        this.preprocess();
    }

    // Preprocess page data before making object available.
    preprocess() {
        // Deserialize JSON data. We accept 'stringified' JSON data as well, even though our API endpoint should always return the expanded JSON.
        // i.e. the difference between { title: 'abc', spec: { 123:4, 567:8 }} and { title: 'abc', spec: '{123:4,567:8}' }.
        for (let key of [ 'spec' ]) {
            if (!this[key]) {
                this[key] = {};
            } else if (typeof this[key] == 'string') {
                console.warn("Forcibly converting stringified JSON to object for field", this.id, key);
                this[key] = JSON.parse(this[key]);
            } else if (typeof this[key] == 'object' && !Array.isArray(this[key])) { 
                // No need to do anything, already in the correct format.
            } else if (typeof this[key] == 'object' && this[key].length <= 0) {
                // We allow only empty 'arrays' as objects. This happens with empty data from the API (e.g. {}).
                this[key] = {};
            } else {
                console.error("Invalid type detected in field", this.id, key, typeof this[key], this[key]);
                throw new Error(`Invalid type detected in field ${this.id} for ${key}: ${typeof this[key]}`);
            }
        }
		// Generate computed keyspec for the field. This is unique and useful as an ID.
		this.keyspec = `${this.getSchema().keyspec}.${this.field}`;
		this.schema_type = this.getSchema().type;
    }

    // Get schema object for this field easily, returning null if not found.
    getSchema() {
        return SchemaCache.getSchemaById(this.schema_id);
    }

    // Get the DevExpress data type for this field. ('string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime')
    // Alternatively, this can get the expected editor to be used. ('dxTextBox' | 'dxTextArea' | 'dxNumberBox' | 'dxCheckBox' | 'dxDateBox')
    // Note that some editors may be overridden if we are in "row" editor type since they don't fit, e.g. dxTextArea.
    getDxDataType(as_expected_editor = false) {
        switch (this.fieldtype) {
            case 'text':
                return as_expected_editor ? 'dxTextBox' : 'string';
            case 'textarea':
                return as_expected_editor ? 'dxTextArea' : 'string';        // Note: in many cases, we force an override to dxTextBox for this due to size constraints (e.g. filter rows, single row editors, etc.)
            case 'number':
                return as_expected_editor ? 'dxNumberBox' : 'number';
            case 'currency':
                return as_expected_editor ? 'dxNumberBox' : 'number';
            case 'datetime':
                return as_expected_editor ? 'dxDateBox' : 'datetime';        // or date?
            case 'relationship':
                return as_expected_editor ? 'dxLookup' : 'object';
            case 'attachment':
            default:
                return as_expected_editor ? 'dxTextBox' : 'object';
        }
    }

    // Get the DevExpress editor options for this field.
    getDxEditorOptions(override_fieldtype = null) {
        switch (override_fieldtype || this.fieldtype) {
            case 'currency':        return { format: { type: 'currency', precision: 2, currency: 'EUR' } };
            case 'textarea':        return { height: 140 };
            case 'relationship':    return {};
            default:                return undefined;
        }
    }

    // Get the relationship schema and opposing field metadata. Each relationship field should have a 'relation' spec entry that points to the relationship schema,
    // and from that we can get the list of schemas involved and field names.
    getRelationshipDetails() {
        if (this.fieldtype != 'relationship' || !this.spec.relation)
            return null;
        let schema = SchemaCache.getSchemaById(this.spec.relation);
        if (!schema)
            return null;
        let thisSchemaInA = schema.meta.aType.includes(this.schema_type);
        let aTypes = schema.meta.aType.map(t => SchemaCache.getSchemaByType(t));
        let bTypes = schema.meta.bType.map(t => SchemaCache.getSchemaByType(t));
        return {
            relationSchema: schema,
            meta: schema.meta,
            oppositeSchemas: thisSchemaInA ? bTypes : aTypes,
            sameSchemas: thisSchemaInA ? aTypes : bTypes,
            oppositeFieldKey: thisSchemaInA ? schema.meta.bKey : schema.meta.aKey,
            sameFieldKey: thisSchemaInA ? schema.meta.aKey : schema.meta.bKey,
            expectManyOpposites: thisSchemaInA ? schema.meta.aDegree == 'many' : schema.meta.bDegree == 'many',
            expectManySames: thisSchemaInA ? schema.meta.bDegree == 'many' : schema.meta.aDegree == 'many',
        };
    }
}

export { fieldTypes, fieldTypeMeta, fieldExtras, fieldNamesReserved, Field, stripFileFieldData };
